import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Play } from "lucide-react";
import { scrollToId } from "@/lib/scroll";
import FundraisingDeck from "@/components/home/FundraisingDeck";
import { useMember } from "@/context/MemberContext";

const LINKS = [
  { label: "Manifesto", id: "manifesto" },
  { label: "Fund Raising", id: "fundraising", deck: true },
  { label: "Episodes", id: "episodes" },
  { label: "Music/Videos", id: "music" },
  { label: "Gallery", id: "gallery" },
  { label: "Founder", id: "founder" },
  { label: "Contact", id: "contact" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [deckOpen, setDeckOpen] = useState(false);
  const { member, setLoginOpen, logout } = useMember();
  useEffect(() => {
    if (new URLSearchParams(window.location.search).get("open") === "fundraising") setDeckOpen(true);
  }, []);
  const handle = (l) => (l.deck ? setDeckOpen(true) : scrollToId(l.id));

  return (
    <>
    <FundraisingDeck open={deckOpen} onClose={() => setDeckOpen(false)} />
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.7, delay: 1.4, ease: [0.16, 1, 0.3, 1] }}
      className="fixed top-0 left-0 right-0 z-50 bg-black/70 backdrop-blur-md border-b border-white/10"
      data-testid="main-navbar"
    >
      <div className="px-4 sm:px-8 lg:px-16 h-16 flex items-center justify-between">
        <button
          data-testid="nav-logo"
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          className="flex items-center gap-2 group"
        >
          <span className="w-3 h-3 bg-blood group-hover:rotate-45 transition-transform duration-300" />
          <span className="font-display font-800 font-extrabold tracking-widest text-lg uppercase">
            Zo <span className="text-blood">Pound</span>
          </span>
        </button>

        <nav className="hidden md:flex items-center gap-8">
          {LINKS.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-link-${l.id}`}
              onClick={() => handle(l)}
              className={`font-mono text-xs uppercase tracking-widest transition-colors duration-300 ${l.deck ? "text-white border border-blood/60 px-3 py-1 hover:bg-blood" : "text-gray-400 hover:text-blood"}`}
            >
              {l.label}
            </button>
          ))}
          {member ? (
            <button data-testid="nav-member-badge" onClick={logout} title={`Signed in as ${member.email} — click to log out`}
              className="font-mono text-[10px] uppercase tracking-widest text-green-400 border border-green-500/40 px-3 py-1 hover:text-white hover:border-white/40 transition-colors">
              Insider ✓
            </button>
          ) : (
            <button data-testid="nav-member-login-button" onClick={() => setLoginOpen(true)}
              className="font-mono text-xs uppercase tracking-widest text-gray-400 hover:text-blood transition-colors">
              Log in
            </button>
          )}
          <button
            data-testid="nav-listen-now-button"
            onClick={() => scrollToId("episodes")}
            className="flex items-center gap-2 bg-blood hover:bg-red-700 text-white font-display font-bold uppercase tracking-wider text-sm px-5 py-2 transition-colors duration-300"
          >
            <Play size={14} fill="currentColor" /> Listen Now
          </button>
        </nav>

        <button
          data-testid="nav-mobile-menu-button"
          className="md:hidden text-gray-200"
          onClick={() => setOpen(!open)}
          aria-label="Toggle menu"
        >
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="md:hidden overflow-hidden border-t border-white/10"
            data-testid="nav-mobile-menu"
          >
            <div className="flex flex-col p-4 gap-3">
              {LINKS.map((l) => (
                <button
                  key={l.id}
                  data-testid={`nav-mobile-link-${l.id}`}
                  onClick={() => { setOpen(false); handle(l); }}
                  className="text-left font-mono text-sm uppercase tracking-widest text-gray-300 hover:text-blood py-2"
                >
                  {l.label}
                </button>
              ))}
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </motion.header>
    </>
  );
}
